import { useListMembers } from '@modusign/openapi-client-user';

import { useSubscription } from '@app/features/subscription/hooks/subscription';
import isTrialPlan from '@app/features/subscription/libs/isTrialPlan';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useInvitationListApi, useWorkspace } from '@app/hooks/workspace';

export function useMemberSeat() {
  const workspaceId = useWorkspaceId();
  const { seat } = useWorkspace();

  const {
    data: memberCount,
    isLoading: isMemberLoading,
    isError: isMemberError,
  } = useListMembers(
    workspaceId,
    { limit: 1, offset: 0 },
    { query: { select: (data) => data.count } }
  );

  const {
    data: invitations,
    isLoading: isInvitationLoading,
    isError: isInvitationError,
  } = useInvitationListApi({
    status: 'ONGOING',
  });

  const currentMemberCount = memberCount || 0;
  const invitationCount = invitations?.length || 0;
  const allMemberCount = currentMemberCount + invitationCount;

  return {
    allMemberCount,
    currentMemberCount,
    invitationCount,
    availableSeatCount: seat.seatLimit - allMemberCount,
    workspaceSeatLimit: seat.seatLimit,
    isLoading: isMemberLoading || isInvitationLoading,
    isError: isMemberError || isInvitationError,
  };
}

const planMaxSeatLimit = (grade?: number) => {
  switch (grade) {
    case 1:
      return 1;
    case 2:
      return 3;
    case 3:
      return 10;
    case -1:
    default:
      return 1000;
  }
};

/** 요금제 정책: https://www.notion.so/modusign/2023-52e69254a4894e5884f5c119a10efaef */
export function useAvailabilityOfMemberSeat() {
  const { seat } = useWorkspace();
  const { data: subscription } = useSubscription();
  const planId = subscription?.plan?.id;
  const planVersion = subscription?.plan?.planVersion;
  const planGrade = subscription?.plan?.grade;
  const hasSeatUnitPrice = subscription?.plan?.seatUnitPrice !== null;
  const isCustomPlan = subscription?.plan?.isCustom;

  const purchasableSeatCount = planMaxSeatLimit(planGrade) - seat.seatLimit;

  return {
    isSeatPurchasable:
      purchasableSeatCount > 0 &&
      (isTrialPlan(planId) ||
        (hasSeatUnitPrice &&
          (planVersion === 0 ||
            (planGrade === 3 && seat.seatLimit < planMaxSeatLimit(3)) ||
            isCustomPlan))),
    isChangeablePlan:
      isTrialPlan(planId) || (planVersion === 1 && (planGrade === 1 || planGrade === 2)),
    hasSeatUnitPrice,
    workspaceSeatLimit: seat.seatLimit,
    planMaxSeatLimit: planMaxSeatLimit(planGrade),
    purchasableSeatCount,
  };
}
